import { Injectable } from '@angular/core';
import { Accordion } from 'src/app/shared/components/accordions-group/accordion/accordion.model';
import { AccordionsGroupComponent } from 'src/app/shared/components/accordions-group/accordions-group.component';
import { AccordionType } from 'src/app/shared/components/accordions-group/models/accordion-type.model';
import { AccordionsGroupData } from 'src/app/shared/components/accordions-group/models/accordions-group-data.model';
import { InfoBoxTab } from 'src/app/shared/components/info-box/info-box-tab.model';
import { NavItem } from 'src/app/shared/components/info-box/nav-item.model';
import { ListComponent } from 'src/app/shared/components/list/list.component';
import { ListData } from 'src/app/shared/components/list/list.model';
import { capitalize } from 'src/app/shared/language-extensions/capitalize.language-extension';
import { distinct } from 'src/app/shared/language-extensions/distinct.language-extension';
import { TranslationService } from 'src/metronic/core/_base/layout';
import { LanguageType } from 'src/metronic/core/_config/i18n/language-type.model';
import { LocalizationName, OtherNames, StudyName } from './models/enrollment-enums.model';
import { Enrollment } from './models/enrollment.model';

@Injectable({
  providedIn: 'root'
})
export class HomeInfoBoxGeneratorService {
  constructor(private translationService: TranslationService) { }

  generateInfoBoxItems(enrollments: Enrollment[], selectedLanguage: LanguageType): InfoBoxTab[] {
    if (enrollments == null) {
      throw new Error('All parameters are required');
    }

    // store all tabs of info box
    const infoBoxTabs: InfoBoxTab[] = [];
    let isFirst = true;

    // get available localizations
    const localizations: string[] = distinct(enrollments.map(e => e.localizationTranslateKey));

    // create info box tab for each localization
    for (const localizationTranslateKey of localizations) {
      const localization = this.instantTranslate(localizationTranslateKey);
      const navItems: NavItem[] = [];

      const enrollmentsInLocalization: Enrollment[] = enrollments.filter(e => e.localizationTranslateKey === localizationTranslateKey);
      const studyNames: string[] = distinct(enrollmentsInLocalization.map(e => e.nameTranslateKey));

      // create nav item for each study name (eg. computer science, new media arts...). Each nav item will consist of multiple accordions.
      for (const studyTranslateKey of studyNames) {
        const navTitle: string = this.instantTranslate(studyTranslateKey);
        const accordions: Accordion[] = [];
        let postgraduateList: ListData[];

        const isCurrentStudyPostgraduate: boolean = studyTranslateKey === StudyName.Postgraduate;

        const enrollmentsOfCertainStudyName: Enrollment[] = enrollmentsInLocalization.filter(e => e.nameTranslateKey === studyTranslateKey);
        const distinctDegrees: string[] = distinct(enrollmentsOfCertainStudyName.map(e => e.degreeTranslateKey));

        // create accordion for each degree. Each accordion will then contain list of available modes and their languages
        for (const degreeTranslateKey of distinctDegrees) {
          const degreeTitle = this.instantTranslate(degreeTranslateKey);
          const listData: ListData[] = [];

          const enrollmentsOfCertainDegree: Enrollment[] = enrollmentsOfCertainStudyName.filter(e => e.degreeTranslateKey === degreeTranslateKey);
          const distinctModes: string[] = distinct(enrollmentsOfCertainDegree.map(e => e.modeTranslateKey));

          // create list of modes and their languages
          for (const modeTranslateKey of distinctModes) {
            const modeTitle = this.instantTranslate(modeTranslateKey);
            const studyUrl: string = this.getStudyUrl(enrollments, localizationTranslateKey, studyTranslateKey, degreeTranslateKey, selectedLanguage, modeTranslateKey);

            const enrollmentsOfCertainMode = enrollmentsOfCertainDegree.filter(e => e.modeTranslateKey === modeTranslateKey);

            const data: { languageTranslateKey: string, postgraduateStudyName: string; }[] =
              enrollmentsOfCertainMode.map(s => ({
                languageTranslateKey: s.languageTranslateKey,
                postgraduateStudyName: s.postgraduateStudyName
              }));

            const languages: string[] = distinct(data.map(s => this.instantTranslate(s.languageTranslateKey)));
            const postgraduateStudiesNames: string[] = distinct(data.map(d => d.postgraduateStudyName));

            if (!isCurrentStudyPostgraduate) {
              listData.push({
                title: `${modeTitle} (${this.generateLanguagesListForMode(languages)})`,
                url: studyUrl
              });
            }
            else {
              postgraduateStudiesNames.forEach(postgraduateStudyName => {
                listData.push({
                  title: `${postgraduateStudyName} [${modeTitle}] (${this.generateLanguagesListForMode(languages)})`,
                  url: studyUrl
                });
              });
            }
          }

          if (!isCurrentStudyPostgraduate) {
            const isAccordionOpen = distinctDegrees.indexOf(degreeTranslateKey) === 0;
            const accordion = this.createAccordion(isAccordionOpen, degreeTitle, listData);

            accordions.push(accordion);
          }
          else { // when current studies are postgraduate display raw list instead of accordion
            postgraduateList = listData.sort((a, b) => a.title.localeCompare(b.title));
          }
        }

        // pre-open an accordion if it contains only one element
        if (this.hasOneElement(accordions)) {
          this.openAccordion(accordions[0]);
        }

        const navItem: NavItem = this.createNavItem(accordions, navTitle, isCurrentStudyPostgraduate, postgraduateList);
        navItems.push(navItem);
      }

      if (localizationTranslateKey === LocalizationName.Warsaw) {
        navItems.push(this.generateMBANavItem());
        navItems.push(this.generateOthersNavItem());
      }

      const infoBoxTab: InfoBoxTab = {
        isActive: isFirst,
        title: localization,
        navItems: navItems
      };

      infoBoxTabs.push(infoBoxTab);
      isFirst = false;
    }

    return infoBoxTabs;
  }

  private getStudyUrl(enrollments: Enrollment[], localizationTranslateKey: string, studyTranslateKey: string, degreeTranslateKey: string, selectedLanguage: LanguageType, modeTranslateKey: string): string {
    return enrollments.find(e => e.localizationTranslateKey === localizationTranslateKey && e.nameTranslateKey === studyTranslateKey && e.degreeTranslateKey === degreeTranslateKey && e.modeTranslateKey === modeTranslateKey).getEnrollmentUrl(selectedLanguage);
  }

  private generateLanguagesListForMode(languages: string[]) {
    return languages.join(' ' + this.instantTranslate('SHARED.WORD-SEPARATOR') + ' ');
  }

  private createAccordion(isOpen: boolean, degreeTitle: string, listData: ListData[]) {
    return {
      isOpen: isOpen,
      title: capitalize(degreeTitle),
      componentToRenderData: {
        componentToRender: ListComponent,
        inputData: listData.sort((a, b) => -a.title.localeCompare(b.title))
      }
    };
  }

  private hasOneElement(accordions: Accordion[]): boolean {
    return accordions.length === 1;
  }

  private openAccordion(accordion: Accordion): void {
    accordion.isOpen = true;
  }

  private createNavItem(accordions: Accordion[], navTitle: string, isPostgraduate: boolean, postgraduateList: ListData[]): NavItem {
    const accordionsGroup: AccordionsGroupData = {
      options: {
        accordionType: AccordionType.BORDERLESS_ON_BACKGROUND,
        shouldAnimateOnEnter: true
      },
      accordionItems: accordions
    };

    const navItem: NavItem = {
      isActive: false,
      titleTranslateKey: navTitle,
      componentToRenderData: !isPostgraduate ?
        {
          componentToRender: AccordionsGroupComponent,
          inputData: accordionsGroup
        }
        :
        {
          componentToRender: ListComponent,
          inputData: postgraduateList
        }
    };

    return navItem;
  }

  private generateOthersNavItem(): NavItem {
    const othersList: ListData[] = [
      {
        title: this.instantTranslate(OtherNames.Courses),
        url: this.getListItemsUrl(OtherNames.Courses)
      }
    ];

    const othersNavItem: NavItem = {
      isActive: false,
      titleTranslateKey: StudyName.Other,
      componentToRenderData: {
        componentToRender: ListComponent,
        inputData: othersList
      }
    };

    return othersNavItem;
  }
  private generateMBANavItem(): NavItem {
    const mbaList: ListData[] = [
      {
        title: this.instantTranslate(OtherNames.MBA),
        url: this.getListItemsUrl(OtherNames.MBA)
      }
    ];

    const othersNavItem: NavItem = {
      isActive: false,
      titleTranslateKey: StudyName.MBA,
      componentToRenderData: {
        componentToRender: ListComponent,
        inputData: mbaList
      }
    };

    return othersNavItem;
  }
  private getListItemsUrl(otherName: OtherNames): string {
    switch (otherName) {
      case OtherNames.Courses:
        return this.translationService.isPolishLanguageSelected() ? 'https://www.pja.edu.pl/studia-podyplomowe-kursy/kursy-internetowe' : 'https://pja.edu.pl/en/dla-kandydata/kursy-internetowe/';
      case OtherNames.MBA:
        return this.translationService.isPolishLanguageSelected() ? 'https://pja.edu.pl/kursy/studia-mba/mba-dla-branzy-it/rekrutacja/' : 'https://pja.edu.pl/en/kursy/studia-mba/mba-dla-branzy-it/rekrutacja/';
      }
  }

  private instantTranslate(translateKey: string): string {
    return this.translationService.getInstantTranslation(translateKey);
  }
}
